import { Calendar, dateFnsLocalizer, View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { startOfWeek, parse, format, getDay } from "date-fns";
import { useContext, useEffect, useMemo, useState } from "react";
import { enUS } from "date-fns/locale";
import {
  Alert,
  Box,
  Modal,
  Typography,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { fetchMeetingEvents } from "../services/meetings.service";
import { DataContext } from "../contexts/DataProvider";
import { toZonedTime } from "date-fns-tz";
import dayjs from "dayjs";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const locales = { "en-US": enUS };
const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// ---------- Custom Toolbar ----------
const CustomToolbar = ({ toolbar, onViewChange, onNavigate }: any) => {
  const handleViewChange = (event: any, newView: string | null) => {
    if (newView !== null) {
      toolbar.onView(newView);
      onViewChange && onViewChange(newView);
    }
  };

  const navigate = (action: "PREV" | "NEXT" | "TODAY" | "DATE") => {
    toolbar.onNavigate(action);
    onNavigate && onNavigate(action, toolbar.view, toolbar.date);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        <IconButton
          sx={{
            width: "32px",
            height: "32px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#f9f9f9" },
          }}
          onClick={() => navigate("PREV")}
        >
          <ChevronLeftIcon />
        </IconButton>

        <span
          style={{
            fontWeight: 600,
            fontSize: 16,
            whiteSpace: "nowrap",
          }}
        >
          {toolbar.label}
        </span>

        <IconButton
          sx={{
            width: "32px",
            height: "32px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#f9f9f9" },
          }}
          onClick={() => navigate("NEXT")}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      <ToggleButtonGroup
        value={toolbar.view}
        exclusive
        onChange={handleViewChange}
        size="small"
        sx={{
          backgroundColor: "#f0f0f0",
          borderRadius: "8px",
          "& .MuiToggleButton-root": {
            textTransform: "capitalize",
            padding: "4px 14px",
            border: "1px solid #B6C1CA",
            backgroundColor: "#f0f0f0",
            color: "#333",
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
          },
          "& .Mui-selected": {
            backgroundColor: "#fff !important",
            color: "#000",
            fontWeight: 600,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
        }}
      >
        <ToggleButton value="month">Month</ToggleButton>
        <ToggleButton value="week">Week</ToggleButton>
        <ToggleButton value="day">Day</ToggleButton>
      </ToggleButtonGroup>
    </div>
  );
};

// ---------- Custom Event ----------
const CustomEvent = ({ event }: any) => (
  <div
    style={{
      background: "#0070B9",
      color: "#fff",
      borderRadius: 6,
      padding: "4px 6px",
      fontSize: "15px",
      marginBottom: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      fontWeight: 400,
      maxWidth: "100%",
    }}
  >
    {event.title}
  </div>
);

// ---------- Main Component ----------
const EventCalendar = ({
  onSelectDate,
  date,
  onNavigate,
  step = 15,
  ...props
}: any) => {
  const [events, setEvents] = useState<any[]>([]);
  const [fetchError, setFetchError] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentView, setCurrentView] = useState<View>("week");
  const userTimezone = getUserTimezone();

  // Calculate timeslots based on step duration
  // timeslots = number of slots per hour (60 / step)
  const timeslots = useMemo(() => Math.max(1, Math.floor(60 / step)), [step]);

  const scrollToTime = useMemo(() => {
    const d = new Date();
    d.setHours(8, 0, 0);
    return d;
  }, []);

  useEffect(() => {
    const getEvents = async () => {
      try {
        const resp: any = await fetchMeetingEvents(userTimezone);
        setEvents(
          (resp.data || []).map((e: any) => {
            const startUtc = new Date(e.start.dateTime);
            const endUtc = new Date(e.end.dateTime);
            return {
              start: toZonedTime(startUtc, userTimezone),
              end: toZonedTime(endUtc, userTimezone),
              title: e.subject,
              description: e.bodyPreview || "No description available",
              organizer: e.organizer?.emailAddress?.name || "Unknown organizer",
              attendees:
                e.attendees?.map((a: any) => a.emailAddress?.name).join(", ") ||
                "No attendees",
            };
          })
        );
      } catch (error) {
        console.error("Failed to fetch events:", error);
        setFetchError(true);
      }
    };
    getEvents();
  }, [userTimezone]);

  const handleEventSelect = (event: any) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  const handleViewChange = (view: View) => setCurrentView(view);

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    onSelectDate && onSelectDate(start, end);
  };

  const dayPropGetter = (day: Date) => {
    if (date) {
      const dayString = format(day, "yyyy-MM-dd");
      const dateString = (date as any).format("YYYY-MM-DD");

      if (dayString === dateString) {
        return {
          className: "rbc-selected-cell",
        };
      }
    }
    return {};
  };

  const slotPropGetter = (slotDate: Date) => {
    if (date && (currentView === "week" || currentView === "day")) {
      const slotDayjs = dayjs(slotDate);
      if (slotDayjs.isSame(date as any, "minute")) {
        return {
          className: "rbc-selected-slot",
        };
      }
    }
    return {};
  };

  return (
    <Box
      bgcolor="white"
      my={5}
      p={2}
      borderRadius={2}
      boxShadow="rgba(0, 0, 0, 0.07) 0px 10px 36px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px"
      {...props}
    >
      {fetchError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Calendar events could not be loaded. Please try refreshing the page.
        </Alert>
      )}
      <style>{`
        /* Ensure the main grid lines appear on the background, not the cell content */
        .rbc-month-view .rbc-day-bg {
            border-left: 1px solid #ddd;
        }
        .rbc-month-view .rbc-month-row {
            border-top: 1px solid #ddd;
        }
        
        .rbc-month-view .rbc-date-cell {
           border: none !important; 
           padding: 5px !important;
           min-height: 40px;
        }

        .rbc-month-view {
           border: 1px solid #ddd;
           border-radius: 4px;
        }
        
        .rbc-month-view .rbc-month-row:first-child {
            border-top: none;
        }
        .rbc-month-view .rbc-day-bg:first-child {
            border-left: none;
        }
        
        /* Event Styling specific to Month View */
        .rbc-month-view .rbc-event {
            padding: 1px 4px;
            font-size: 0.75em;
            border-radius: 3px;
            margin-bottom: 2px;
            background-color: #0070B9;
            color: #fff;
            border: none;
        }


        /* Highlight selected DAY (Month View) */
        .rbc-selected-cell {
          background-color: #e3f2fd !important;
          border: 1px solid #0d47a1 !important;
          box-shadow: inset 0 0 0 1px #42a5f5 !important;
        }

        /* Highlight selected TIME SLOT (Week/Day View) */
        .rbc-time-slot.rbc-selected-slot {
            background-color: #bbdefb !important;
            border-top: 1px solid #1976d2 !important;
            border-bottom: 1px solid #1976d2 !important;
        }
        
        /* Standard RBC cleanups */
        .rbc-today {
          background-color: transparent !important;
        }
        
        /* Month View "Show More" link */
        .rbc-month-view .rbc-show-more {
            color: #005a9e;
            font-size: 0.7em;
            font-weight: bold;
            margin-top: 2px;
        }

        /* Hide the empty all-day row */
        .rbc-allday-cell { display: none !important; }
        
        .rbc-time-header-content {
        border-left: none !important;
        }
        
        /* Fix week/day view column alignment */
        .rbc-time-header-gutter {
          padding: 0 !important;
          width: 70px !important;
          min-width: 70px !important;
          max-width: 70px !important;
          border-right: 1px solid #ddd;
        }
        .rbc-time-view .rbc-time-gutter {
          border-right: 1px solid #ddd;
          width: 70px !important;
          min-width: 70px !important;
          max-width: 70px !important;
        }
      `}</style>

      <Calendar
        localizer={localizer}
        view={currentView}
        onView={(view: View) => setCurrentView(view)}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 650 }}
        onSelectEvent={handleEventSelect}
        selectable
        onSelectSlot={handleSelectSlot}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        date={date ? date.toDate() : new Date()}
        components={{
          toolbar: (toolbarProps) => (
            <CustomToolbar
              toolbar={toolbarProps}
              onViewChange={handleViewChange}
              onNavigate={onNavigate}
            />
          ),
          event: CustomEvent,
        }}
        popup
        step={step}
        timeslots={timeslots}
        scrollToTime={scrollToTime}
      />

      {/* Event Modal */}
      <Modal open={modalOpen} onClose={closeModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 320,
            maxWidth: 400,
          }}
        >
          <Typography variant="h6" fontWeight={700} mb={2}>
            {selectedEvent?.title}
          </Typography>
          <Typography variant="body2" mb={1}>
            <strong>Organizer:</strong> {selectedEvent?.organizer}
          </Typography>
          <Typography variant="body2" mb={1}>
            <strong>Attendees:</strong> {selectedEvent?.attendees}
          </Typography>
          <Typography variant="body2" mb={2}>
            <strong>Description:</strong> {selectedEvent?.description}
          </Typography>
          <Button onClick={closeModal} variant="outlined" fullWidth>
            Close
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default EventCalendar;